import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Loader2, Mic2 } from 'lucide-react';
import { absUrl, errMsg, listActors, mtvLead } from '../../../lib/aiStudioApi';
import { cn } from '../../../lib/utils';

/** The lead singer joins the cast (face + look locked) before the story is written. */
export function LeadSingerCard({ project, onChanged }) {
  const [list, setList] = useState(null);
  const [saving, setSaving] = useState('');
  const kitSize = (project.show_kit || []).length;
  useEffect(() => { listActors().then((d) => setList(d.actors || [])).catch(() => setList([])); }, [kitSize]);
  const lead = (project.show_kit || []).find((k) => k.lead_singer);
  const pick = async (a) => {
    setSaving(a.id);
    try { await mtvLead(project.id, a.id); toast.success(`${a.name} is the lead singer`); onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setSaving(''); }
  };
  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-900/40 p-5" data-testid="mtv-lead-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-neutral-100"><Mic2 size={15} className="text-amber-300" /> Lead singer
        {lead && <span className="ml-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-normal text-amber-200" data-testid="mtv-lead-current">{lead.name}</span>}</div>
      <p className="mb-4 text-xs text-neutral-400">Pick who performs the song from your actor roster. Their face and look are locked into every performance shot. Add other characters and places below.</p>
      {list === null ? <Loader2 size={16} className="animate-spin text-neutral-500" /> : list.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-700 p-4 text-xs text-neutral-400" data-testid="mtv-lead-empty">No actors yet — open the actor roster in Cast & locations below, create your singer, then pick them here.</div>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6" data-testid="mtv-lead-grid">
          {list.map((a) => {
            const on = lead?.name === a.name;
            return (
              <button key={a.id} type="button" onClick={() => !on && pick(a)} disabled={!!saving} data-testid={`mtv-lead-${a.id}`}
                className={cn('group relative overflow-hidden rounded-lg border text-left transition-[border-color,transform] duration-200 hover:-translate-y-0.5',
                  on ? 'border-amber-400 ring-1 ring-amber-400/50' : 'border-neutral-800 hover:border-neutral-600')}>
                <div className="aspect-[3/4] bg-neutral-900">
                  {a.face_url ? <img src={absUrl(a.face_url)} alt={a.name} className="h-full w-full object-cover" loading="lazy" />
                    : <div className="flex h-full items-center justify-center text-2xl font-semibold text-neutral-600">{a.name?.[0] || '?'}</div>}
                </div>
                <div className="px-2 py-1.5"><div className="truncate text-xs font-medium text-neutral-100">{a.name}</div>
                  <div className="truncate text-[10px] text-neutral-500">{[a.gender, a.age].filter(Boolean).join(' · ')}</div></div>
                {(on || saving === a.id) && <span className="absolute right-1.5 top-1.5 rounded-full bg-amber-500 p-1 text-neutral-900">{saving === a.id ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
